/**
 * The events the components raise, typed once for the whole app.
 *
 * Declaring them on `HTMLElementEventMap` is what lets an ordinary
 * `addEventListener("meld-add", ...)` hand its listener a typed detail, the
 * same way the DOM's own events do - no framework event bus in between.
 *
 * Events bubble but, by default, are not composed: one raised inside a
 * component's shadow tree stops at that shadow root, so the component that
 * owns the child hears it and nothing further out does. A component passes
 * `composed` only for a request its parent has nothing to add to and the app
 * itself must answer.
 */

import type { MeldableRank } from "@ryzun/haf-saloon-game-engine";
import type { PlayerCount, TeamColor } from "../server/protocol.js";

export type TableauKind = "books" | "melds";

export interface CardDetail {
    readonly cardId: string;
}

export interface RankDetail {
    readonly rank: MeldableRank;
}

export interface InspectDetail {
    readonly teamId: string;
    readonly kind: TableauKind;
}

export interface FriendDetail {
    readonly id: string;
}

export interface FriendNameDetail {
    readonly name: string;
}

export interface ProfileChoice {
    readonly name: string;
    readonly color: TeamColor;
    readonly fourColorDeck: boolean;
}

export interface SeatDetail {
    readonly seatIndex: number;
}

export interface PlayerCountDetail {
    readonly playerCount: PlayerCount;
}

export interface ColorDetail {
    readonly color: TeamColor;
}

export interface ActionDetail {
    /** Untyped on purpose: the server parses every action as untrusted. */
    readonly action: Readonly<Record<string, unknown>>;
}

/** Every event the app's components raise, by name. */
export interface AppEventMap {
    "card-toggle": CustomEvent<CardDetail>;
    "clear-selection": CustomEvent<null>;
    "discard": CustomEvent<null>;
    "draw-stock": CustomEvent<null>;
    "edit-profile": CustomEvent<null>;
    "friend-accept": CustomEvent<FriendDetail>;
    "friend-add": CustomEvent<FriendNameDetail>;
    "friend-decline": CustomEvent<FriendDetail>;
    "friend-remove": CustomEvent<FriendDetail>;
    "game-action": CustomEvent<ActionDetail>;
    "game-start": CustomEvent<null>;
    "lay-down": CustomEvent<null>;
    "lay-down-clear": CustomEvent<null>;
    "leave-table": CustomEvent<null>;
    "meld-add": CustomEvent<RankDetail>;
    "meld-create": CustomEvent<null>;
    "meld-inspect": CustomEvent<InspectDetail>;
    "meld-refuse": CustomEvent<RankDetail>;
    "meld-unstage": CustomEvent<RankDetail>;
    "names-requested": CustomEvent<null>;
    "play-requested": CustomEvent<null>;
    "player-count-change": CustomEvent<PlayerCountDetail>;
    "profile-cancel": CustomEvent<null>;
    "profile-save": CustomEvent<ProfileChoice>;
    "seat-take": CustomEvent<SeatDetail>;
    "take-discard": CustomEvent<null>;
    "team-color-change": CustomEvent<ColorDetail>;
}

/*
 * Merged into both maps: a component hears its children through its shadow
 * root, which types its listeners from ShadowRootEventMap rather than from
 * HTMLElementEventMap.
 */
declare global {
    interface HTMLElementEventMap extends AppEventMap {}

    interface ShadowRootEventMap extends AppEventMap {}
}

export type DetailOf<K extends keyof AppEventMap> = AppEventMap[K] extends CustomEvent<infer D> ? D : never;

export function emit<K extends keyof AppEventMap>(
    target: EventTarget,
    type: K,
    detail: DetailOf<K>,
    composed = false
): void {
    target.dispatchEvent(new CustomEvent(type, { bubbles: true, composed, detail }));
}
